import type { CustomerAccount, CustomerAccountRow } from "./types";

// Words for the receipt's payment-account block (deposit at request,
// top-ups, payment requests). Port of v1's
// apps/widget/src/components/BookingReceiptCard/accountPaymentCopy.ts: the
// figures are the API's (`toCustomerAccountView`), these only word them,
// the same way Junno quotes them.

type Fmt = { money: (pence: number) => string; date: (iso: string) => string };

export type RowTone = "paid" | "due" | "overdue" | "muted";

/** One row's status, as the customer reads it. */
export function describeRowStatus(
  row: CustomerAccountRow,
  fmt: Fmt,
  opts: { requested: boolean; now?: Date },
): { text: string; tone: RowTone } {
  const now = opts.now ?? new Date();
  switch (row.status) {
    case "paid":
      return { text: "Paid", tone: "paid" };
    case "waived":
      return { text: "Not needed", tone: "muted" };
    case "voided":
      return { text: "Cancelled", tone: "muted" };
  }
  if (opts.requested) return { text: "Due once your request is confirmed", tone: "muted" };
  const partly = row.paidAmountPence > 0 ? `${fmt.money(row.remainingPence)} left · ` : "";
  const overdue = row.status === "overdue" || (!!row.dueDate && new Date(row.dueDate).getTime() < now.getTime());
  if (overdue) {
    return { text: `${partly}Overdue${row.dueDate ? ` since ${fmt.date(row.dueDate)}` : ""}`, tone: "overdue" };
  }
  const where = row.collection === "in_person" ? " · paid at the venue" : "";
  return { text: `${partly}${row.dueDate ? `Due ${fmt.date(row.dueDate)}` : "Due later"}${where}`, tone: "due" };
}

/**
 * The sentence under the figures: a pending request with its deposit paid,
 * a refund the school owes, what is payable now, or what is left to pay at
 * the venue. Null when the booking is settled.
 */
export function describeAccountSummary(account: CustomerAccount, fmt: Fmt): string | null {
  if (account.requested) {
    return account.paidPence > 0
      ? "Request pending — deposit paid. Nothing more is due until your booking is confirmed."
      : "Request pending. Nothing is due until your booking is confirmed.";
  }
  if (account.overpaidPence > 0) {
    return `You've paid ${fmt.money(account.overpaidPence)} more than your booking now costs — the organiser will refund the difference.`;
  }
  if (account.payNow) {
    return account.payNow.amountPence === account.outstandingPence
      ? null
      : `Next payment: ${fmt.money(account.payNow.amountPence)}${account.payNow.dueDate ? `, due ${fmt.date(account.payNow.dueDate)}` : ""}.`;
  }
  if (account.owedInPerson) return `The remaining ${fmt.money(account.outstandingPence)} is paid at the venue.`;
  return null;
}

/** What a failed "Pay now" tells the customer. 401: not signed in; 404:
 * signed in, but not as the person who booked. */
export function describePayNowError(status: number | undefined, serverMessage: string | undefined): string {
  if (status === 401) return "Sign in with the email you booked with to pay online.";
  if (status === 404) return "Sign in with the email you booked with to pay online — this booking isn't linked to your account.";
  return serverMessage || "We couldn't open the payment page. Please try again, or use the link in your email.";
}
